import { useMutation } from '@tanstack/react-query';
import { customerQueries } from '@/queries/customer';
import { waitForOperation } from '@/ws';
import { api } from '@/api';
import { CreateTxUser, PaginatedCustomerListQueryFilters, CustomerMT, UpdateTxUser } from '@/api/apis/customer';
import { queryClient } from '@/queries';
import { getCustomerItemUpdater, getCustomerListItemUpdater } from '@/queries/customer/utils';
import { userQueries } from '@/queries/user';
import { customerQueryKeys } from '@/queries/customer/customer.keys';

export const useCreateCustomerMutation = () => {
  return useMutation({
    mutationFn: (variables: CreateTxUser) => api.customer.createCustomer(variables),
    onSuccess: async ({ correlationId }) => {
      await waitForOperation(correlationId, 3000);
      await queryClient.invalidateQueries({ queryKey: customerQueryKeys.getList() });
      await queryClient.invalidateQueries({ queryKey: customerQueryKeys.getInfiniteList() });
    },
  });
};

export const useCreateOrAssignCustomerMutation = () => {
  return useMutation({
    mutationFn: (variables: CreateTxUser) => api.customer.createOrAssignCustomer(variables),
    onSuccess: async ({ correlationId }) => {
      await waitForOperation(correlationId, 3000);
      await queryClient.invalidateQueries({ queryKey: customerQueryKeys.getList() });
      await queryClient.invalidateQueries({ queryKey: customerQueryKeys.getInfiniteList() });
    },
  });
};

export const useUpdateUserCustomerMutation = (profileUserId?: string) => {
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<UpdateTxUser> & { id: string }) =>
      api.customer.updateCustomerById(id, payload),
    meta: {
      invalidates: [customerQueryKeys.getList(), customerQueryKeys.getInfiniteList()],
    },
    onSuccess: (data: CustomerMT, variables) => {
      if (data.userId === profileUserId) {
        queryClient.refetchQueries({ queryKey: userQueries.profile().queryKey });
      }
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.getItem(variables.id) });
    },
  });
};

export const useUpdateMyCustomerMutation = () => {
  return useMutation({
    mutationFn: (payload: Partial<CustomerMT>) => api.customer.updateMyCustomer(payload),
    meta: {
      invalidates: [customerQueryKeys.getList(), customerQueryKeys.getInfiniteList()],
    },
    onSuccess: (customer) => {
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.getItem(customer.id) });
      queryClient.invalidateQueries({ queryKey: userQueries.profile().queryKey });
    },
  });
};

export const useEnableCustomerOptimisticMutation = (filterQuery: PaginatedCustomerListQueryFilters) => {
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.customer.updateCustomerById(id, { enabled }),
    meta: {
      invalidates: [customerQueryKeys.getInfiniteList()],
    },
    onMutate: ({ id, enabled }) => {
      const body = { enabled };
      const prevSnapshot = {
        list: queryClient.getQueryData(customerQueries.list(filterQuery).queryKey),
        item: queryClient.getQueryData(customerQueries.item(id).queryKey),
        composedItem: queryClient.getQueryData(customerQueries.composedItem(id).queryKey),
      };
      queryClient.setQueryData(customerQueries.list(filterQuery).queryKey, getCustomerListItemUpdater(id, body));
      queryClient.setQueryData(customerQueries.item(id).queryKey, getCustomerItemUpdater(body));
      queryClient.setQueryData(customerQueries.composedItem(id).queryKey, getCustomerItemUpdater(body));

      return prevSnapshot;
    },
    onError: (_e, { id }, context) => {
      queryClient.setQueryData(customerQueries.list(filterQuery).queryKey, context?.list);
      queryClient.setQueryData(customerQueries.item(id).queryKey, context?.item);
      queryClient.setQueryData(customerQueries.composedItem(id).queryKey, context?.item);
    },
  });
};
